import { useTranslation } from 'react-i18next';
import { Accordion, AccordionDetails, AccordionSummary, Button, Divider, Stack, styled, Text } from 'shell/components';
import {
    DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
    DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
    DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
    DEFAULT_FEEDBACK_TONE,
    DEFAULT_INLINE_COMMENTS_COMMENT_LENGTH,
    DEFAULT_INLINE_COMMENTS_LANGUAGE_LEVEL,
    DEFAULT_INLINE_COMMENTS_MAX_COMMENTS_PER_PAGE,
    DEFAULT_INLINE_COMMENTS_TONE,
} from 'utils/constants';
import { getLanguageLevelText, getSuggestionLengthText, getToneText } from 'utils/helper';

const hasCustomGradingSettings = (assignment) => {
    return Boolean(assignment.ai_grading_settings) || Boolean(assignment.ai_grading_context);
};

const AssignmentSettings = ({ assignment, onClickViewGradingSettings }) => {
    const { t } = useTranslation('assignment');
    return (
        <>
            <Stack divider={<Divider />} spacing={2}>
                <Text>{t('grading-section.title', 'Grading')}</Text>
                <Stack spacing={2}>
                    <Stack alignItems={'center'} direction={'row'} spacing={1}>
                        <Text>
                            {t(
                                'grading-section.final-submission-grading-suggestions',
                                'Final Submission Grading Suggestions'
                            )}
                            :
                        </Text>
                        <Text sx={{ flexShrink: 0 }} color={'light'}>
                            {assignment.ai_grading_enabled
                                ? t('common:ui.enabled', 'Enabled')
                                : t('common:ui.not-enabled', 'Not Enabled')}
                        </Text>
                    </Stack>
                    <Accordion>
                        <AccordionSummary sx={{ width: 'fit-content' }}>
                            <Text>{t('advanced-grading.title', 'Advanced Grading Settings')}</Text>
                        </AccordionSummary>
                        <AccordionDetails sx={{ mt: 2 }}>
                            <CustomStack spacing={3}>
                                {hasCustomGradingSettings(assignment) ? (
                                    <Stack direction={'row'} spacing={2}>
                                        <Text color={'light'}>
                                            {t('advanced-settings', 'Advanced settings')}:{' '}
                                            {t('custom-grading-instructions', 'Custom grading instructions')}
                                        </Text>
                                        <Button variant={'borderless'} onClick={onClickViewGradingSettings}>
                                            {t('common:controls.view', 'View')}
                                        </Button>
                                    </Stack>
                                ) : (
                                    <Text color={'light'}>
                                        {t('advanced-settings', 'Advanced settings')}:{' '}
                                        {t('system-default', 'System Default')}
                                    </Text>
                                )}
                            </CustomStack>
                        </AccordionDetails>
                    </Accordion>
                </Stack>
            </Stack>
            <Stack divider={<Divider />} spacing={2}>
                <Text>{t('feedback-section.feedback-label', 'Feedback')}</Text>
                <Stack spacing={2}>
                    <Stack alignItems={'center'} direction={'row'} spacing={1}>
                        <Text>
                            {t(
                                'feedback-section.final-submission-feedback-suggestions',
                                'Final Submission Feedback Suggestions'
                            )}
                            :
                        </Text>
                        <Text sx={{ flexShrink: 0 }} color={'light'}>
                            {assignment.ai_feedback_enabled
                                ? t('common:ui.enabled', 'Enabled')
                                : t('common:ui.not-enabled', 'Not Enabled')}
                        </Text>
                    </Stack>
                    {process.env.REACT_APP_AUTH !== 'PASSWORD' && (
                        <Stack alignItems={'center'} direction={'row'} spacing={1}>
                            <Text>{t('feedback-section.instant-feedback', 'Instant Feedback for Students')}:</Text>
                            <Text color={'light'}>
                                {assignment.ai_student_feedback_enabled
                                    ? t('common:ui.enabled', 'Enabled')
                                    : assignment.ai_feedback_settings?.structure === 'templated'
                                      ? t('common:ui.not-available', 'Not Available')
                                      : t('common:ui.not-enabled', 'Not Enabled')}
                            </Text>
                        </Stack>
                    )}
                    <Accordion>
                        <AccordionSummary sx={{ width: 'fit-content' }}>
                            <Text>{t('feedback-section.settings.title', 'Advanced Feedback Settings')}</Text>
                        </AccordionSummary>
                        <AccordionDetails sx={{ mt: 2 }}>
                            <CustomStack spacing={3}>
                                <Stack>
                                    <Text color={'light'}>
                                        {t('advanced-settings', 'Advanced settings')}:{' '}
                                        {assignment.ai_feedback_settings?.structure === 'rubric' &&
                                            t('feedback-section.rubric-based-feedback', 'Rubric-based feedback')}
                                        {assignment.ai_feedback_settings?.structure === 'system' &&
                                            t('feedback-section.system-based-feedback', 'General & detailed feedback')}
                                        {assignment.ai_feedback_settings?.structure === 'templated' &&
                                            t('feedback-section.templated-feedback', 'Smart Feedback Templates')}
                                        {assignment.ai_feedback_settings?.structure === 'custom' &&
                                            t('feedback-section.custom-feedback', 'Custom feedback structure')}
                                    </Text>
                                    {['rubric', 'system'].includes(assignment.ai_feedback_settings?.structure) && (
                                        <Stack sx={{ m: 0, listStyleType: '"- "' }} component={'ul'}>
                                            {assignment.ai_feedback_settings?.structure === 'system' && (
                                                <Text color={'light'} component={'li'}>
                                                    {t(
                                                        'feedback-section.settings.number-of-suggestions',
                                                        'Number of suggestions'
                                                    )}
                                                    :{' '}
                                                    {assignment.ai_feedback_settings?.num_suggestions ||
                                                        DEFAULT_FEEDBACK_NUM_SUGGESTIONS}
                                                </Text>
                                            )}
                                            <Text color={'light'} component={'li'}>
                                                {t(
                                                    'feedback-section.settings.suggestion-length',
                                                    'Length of each suggestion'
                                                )}
                                                :{' '}
                                                {getSuggestionLengthText(
                                                    assignment.ai_feedback_settings?.suggestion_length ||
                                                        DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
                                                    t
                                                )}
                                            </Text>
                                            <Text color={'light'} component={'li'}>
                                                {t('feedback-settings.language-level.title', 'Language level')}:{' '}
                                                {getLanguageLevelText(
                                                    assignment.ai_feedback_settings?.language_level ||
                                                        DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
                                                    t
                                                )}
                                            </Text>
                                            <Text color={'light'} component={'li'}>
                                                {t('feedback-settings.tone.feedback-title', 'Feedback tone')}:{' '}
                                                {getToneText(
                                                    assignment.ai_feedback_settings?.tone || DEFAULT_FEEDBACK_TONE,
                                                    t
                                                )}
                                            </Text>
                                        </Stack>
                                    )}
                                </Stack>
                                {['rubric', 'system'].includes(assignment.ai_feedback_settings?.structure) && (
                                    <Text color={'light'}>
                                        {t('additional-instructions', 'Additional instructions')}:
                                        {assignment.ai_feedback_context ? (
                                            <>
                                                <Text color={'light'} component={'i'} rich>
                                                    {assignment.ai_feedback_context}
                                                </Text>
                                            </>
                                        ) : (
                                            <Text color={'light'} component={'i'}>
                                                {' '}
                                                {t('common:ui.none', 'None')}
                                            </Text>
                                        )}
                                    </Text>
                                )}
                                {assignment.ai_feedback_settings?.structure === 'custom' && (
                                    <Text color={'light'} rich>
                                        {assignment.ai_feedback_settings?.custom_instructions}
                                    </Text>
                                )}
                            </CustomStack>
                        </AccordionDetails>
                    </Accordion>
                </Stack>
            </Stack>
            <Stack divider={<Divider />} spacing={2}>
                <Text>{t('inline-comments-section.title', 'Inline Comments')}</Text>
                <Stack spacing={2}>
                    <Stack alignItems={'center'} direction={'row'} spacing={1}>
                        <Text>
                            {t('inline-comments-section.generate-inline-comments', 'Generate Inline Comments')}:
                        </Text>
                        <Text sx={{ flexShrink: 0 }} color={'light'}>
                            {assignment.ai_inline_comments_enabled
                                ? t('common:ui.enabled', 'Enabled')
                                : t('common:ui.not-enabled', 'Not Enabled')}
                        </Text>
                    </Stack>
                    <Accordion>
                        <AccordionSummary sx={{ width: 'fit-content' }}>
                            <Text>
                                {t('inline-comments-section.settings.title', 'Advanced Inline Comments Settings')}
                            </Text>
                        </AccordionSummary>
                        <AccordionDetails sx={{ mt: 2 }}>
                            <CustomStack spacing={3}>
                                <Stack sx={{ m: 0, listStyleType: '"- "' }} component={'ul'}>
                                    <Text color={'light'} component={'li'}>
                                        {t(
                                            'inline-comments-section.settings.max-comments',
                                            'Maximum Comments per Page'
                                        )}
                                        :{' '}
                                        {assignment.ai_inline_comments_settings?.max_comments_per_page ||
                                            DEFAULT_INLINE_COMMENTS_MAX_COMMENTS_PER_PAGE}
                                    </Text>
                                    <Text color={'light'} component={'li'}>
                                        {t('inline-comments-section.settings.comment-length', 'Length of each comment')}
                                        :{' '}
                                        {getSuggestionLengthText(
                                            assignment.ai_inline_comments_settings?.comment_length ||
                                                DEFAULT_INLINE_COMMENTS_COMMENT_LENGTH,
                                            t
                                        )}
                                    </Text>
                                    <Text color={'light'} component={'li'}>
                                        {t('feedback-settings.language-level.title', 'Language level')}:{' '}
                                        {getLanguageLevelText(
                                            assignment.ai_inline_comments_settings?.language_level ||
                                                DEFAULT_INLINE_COMMENTS_LANGUAGE_LEVEL,
                                            t
                                        )}
                                    </Text>
                                    <Text color={'light'} component={'li'}>
                                        {t('feedback-settings.tone.comment-title', 'Commentary tone')}:{' '}
                                        {getToneText(
                                            assignment.ai_inline_comments_settings?.tone ||
                                                DEFAULT_INLINE_COMMENTS_TONE,
                                            t
                                        )}
                                    </Text>
                                </Stack>
                                <Text color={'light'}>
                                    {t('additional-instructions', 'Additional instructions')}:{' '}
                                    {assignment.ai_inline_comments_context ? (
                                        <>
                                            <Text color={'light'} component={'i'} rich>
                                                {assignment.ai_inline_comments_context}
                                            </Text>
                                        </>
                                    ) : (
                                        <Text color={'light'} component={'i'}>
                                            {' '}
                                            {t('common:ui.none', 'None')}
                                        </Text>
                                    )}
                                </Text>
                            </CustomStack>
                        </AccordionDetails>
                    </Accordion>
                </Stack>
            </Stack>
        </>
    );
};

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        borderRadius: '4px',
        padding: theme.spacing(3),
        background: theme.palette.ensightful.neutral[50],
    };
});

export default AssignmentSettings;
